import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "@phosphor-icons/react";
import SectionLabel from "../components/SectionLabel";
import { SERVICES, PROCESS, MEDIA } from "../lib/constants";
import { ease } from "../lib/motion";

const Hero = () => (
  <section className="relative pt-44 pb-16 md:pt-52 md:pb-24 text-center">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1400px] mx-auto px-6 md:px-10"
    >
      <p className="overline mb-6">Our expertise</p>
      <h1
        className="font-display tracking-tighter leading-[0.95] text-white mx-auto max-w-5xl"
        style={{ fontSize: "clamp(48px, 8vw, 128px)", fontWeight: 600 }}
        data-testid="services-title"
      >
        Six disciplines. One <span className="shimmer italic">studio.</span>
      </h1>
      <p className="mt-8 text-lg text-[#a0abc0] max-w-2xl mx-auto leading-relaxed">
        We don't offer everything to everyone. These are the practices we've
        invested decades in, and where we believe we do our best work.
      </p>
    </motion.div>
  </section>
);

const ServiceRow = ({ service, index }) => {
  const reversed = index % 2 === 1;
  const img = index % 2 === 0 ? MEDIA.serviceAbstract : MEDIA.serviceServers;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="py-20 md:py-28 border-t border-white/5"
      data-testid={`service-row-${service.id}`}
    >
      <div
        className={`max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center ${
          reversed ? "md:[&>*:first-child]:order-2" : ""
        }`}
      >
        <div className="md:col-span-6">
          <div className="relative h-[360px] md:h-[460px] rounded-2xl overflow-hidden border border-white/10 group">
            <img
              src={img}
              alt={service.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#050814]/40 to-[#050814]/70" />
            <span className="absolute top-6 left-6 font-mono text-xs text-[#d4af37] tracking-widest">
              {service.number}
            </span>
          </div>
        </div>

        <div className="md:col-span-6">
          <p className="overline mb-5">Service {service.number}</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight text-white leading-[1.05] mb-6">
            {service.title}
          </h2>
          <p className="text-[#a0abc0] text-lg leading-relaxed mb-8 max-w-lg">
            {service.description}
          </p>

          <ul className="space-y-3 mb-10">
            {service.deliverables.map((d) => (
              <li key={d} className="flex items-center gap-3 text-white">
                <span className="flex items-center justify-center w-6 h-6 rounded-full border border-[#d4af37]/50">
                  <Check size={14} weight="bold" className="text-[#d4af37]" />
                </span>
                <span className="text-sm md:text-base">{d}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/contact"
            data-testid={`service-cta-${service.id}`}
            className="inline-flex items-center gap-2 text-[#d4af37] border-b border-[#d4af37] pb-1 hover:gap-4 transition-all duration-300"
          >
            Discuss this engagement <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

const Process = () => (
  <section className="py-24 md:py-36 bg-[#050814] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionLabel number="07" label="How we work" />
      <h2 className="font-display text-3xl md:text-5xl tracking-tight text-white max-w-3xl mb-20 leading-[1.05]">
        A process refined over eight years and one hundred fifty engagements.
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {PROCESS.map((p, i) => (
          <motion.div
            key={p.step}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1, ease }}
            className="relative"
          >
            <div className="absolute -top-3 left-0 right-0 h-px bg-gradient-to-r from-[#d4af37]/60 to-transparent" />
            <p className="font-mono text-xs text-[#d4af37] tracking-widest mt-6">
              STEP {p.step}
            </p>
            <h3 className="font-display text-2xl md:text-3xl text-white mt-4 mb-4 tracking-tight">
              {p.title}
            </h3>
            <p className="text-[#a0abc0] text-sm leading-relaxed">{p.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Engage = () => (
  <section className="py-28 md:py-40 bg-[#0a0f1d] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
      <p className="overline mb-6">Engagement models</p>
      <h2 className="font-display text-3xl md:text-5xl tracking-tight text-white max-w-3xl mx-auto leading-[1.05]">
        Project, retainer or fractional. <br />
        <span className="text-[#d4af37] italic">Whichever fits.</span>
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left">
        {[
          { name: "Fixed-scope project", desc: "Defined deliverables, fixed price, fixed timeline." },
          { name: "Continuous retainer", desc: "Monthly engineering capacity, no surprises." },
          { name: "Fractional CTO", desc: "Senior leadership, embedded with your team." },
        ].map((e, i) => (
          <motion.div
            key={e.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1, ease }}
            className="rounded-2xl border border-white/5 bg-[#050814] p-8 hover-lift hover:border-[#d4af37]/30"
          >
            <p className="font-mono text-xs text-[#d4af37] tracking-widest mb-4">
              0{i + 1}
            </p>
            <h3 className="font-display text-2xl text-white tracking-tight mb-3">
              {e.name}
            </h3>
            <p className="text-[#a0abc0] text-sm leading-relaxed">{e.desc}</p>
          </motion.div>
        ))}
      </div>

      <Link
        to="/contact"
        data-testid="services-bottom-cta"
        className="inline-flex items-center gap-3 mt-16 bg-[#d4af37] hover:bg-[#f2e3c6] text-[#050814] font-semibold px-10 py-5 rounded-full transition-all duration-300 hover:gap-5"
      >
        Request a proposal <ArrowRight size={18} weight="bold" />
      </Link>
    </div>
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <Hero />
      <div>
        {SERVICES.map((s, i) => (
          <ServiceRow key={s.id} service={s} index={i} />
        ))}
      </div>
      <Process />
      <Engage />
    </div>
  );
}
